import { useState, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion, useScroll, useTransform, AnimatePresence, useReducedMotion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Plus, ArrowUpRight, ArrowRight, Check, ChevronLeft, ChevronRight, ChevronDown, X, Menu, Sparkles, Mail, Users, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLenis, Reveal } from "@/site/Reveal";
import { IMG, CHAPTERS, ATTRACTIONS, TEAM, FAQS, MOMENTS, INCLUDE_GROUPS, INCLUDES_INTRO, INCLUDES_GALLERY, INCLUDED_CHECKLIST, JOURNEY, GALLERY, LAYERS, OUTCOMES, TESTIMONIALS } from "@/site/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  ["Intention", "intention"],
  ["Transformation", "transformation"],
  ["The Guides", "team"],
  ["Location", "location"],
  ["The Days", "attractions"],
  ["The Journey", "journey"],
  ["Included", "includes"],
  ["Our Story", "story"],
  ["Voices", "voices"],
  ["Questions", "faq"],
];

const heroLine = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1.1, delay: 0.3 + i * 0.14, ease: [0.16, 1, 0.3, 1] } }),
};

function scrollTo(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
}

function ApplyCta({ bg = "#F7F5F0", dark = false }) {
  return (
    <div className="flex justify-center px-6 py-16 lg:py-20" style={{ backgroundColor: bg }}>
      <button onClick={() => scrollTo("reserve")} data-testid="mid-apply-cta"
        className={`reserve-cta inline-flex items-center gap-2 px-8 py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] ${dark ? "bg-[#B85C38] text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#2C2A28]" : "bg-[#2C2A28] text-[#F7F5F0] hover:bg-[#B85C38]"}`}>
        Step toward Tuscany <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function Nav() {
  const [active, setActive] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const els = NAV.map(([, id]) => document.getElementById(id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.2, 0.5, 1] }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F7F5F0]/70 border-b hairline">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <button onClick={() => scrollTo("top")} data-testid="nav-logo" className="flex items-center gap-2.5 font-serif text-xl tracking-tight text-[#2C2A28] shrink-0 whitespace-nowrap">
          <img src="/images/logo-mark.png" alt="The Ageless Woman — radiant profile mark" className="h-12 w-12 object-contain" />
          The Ageless Woman
        </button>
        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6">
          {NAV.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => scrollTo(id)}
              className={`font-mono-a text-[11px] uppercase tracking-[0.14em] whitespace-nowrap transition-colors ${active === id ? "text-[#B85C38]" : "text-[#595550] hover:text-[#B85C38]"}`}>
              {label}
              <span className={`block h-px mt-1 transition-all duration-300 ${active === id ? "bg-[#B85C38] w-full" : "bg-transparent w-0"}`} />
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-1.5">
          <button data-testid="nav-reserve" onClick={() => scrollTo("reserve")}
            className="reserve-cta font-mono-a text-[11px] uppercase tracking-[0.18em] bg-[#2C2A28] text-[#F7F5F0] px-4 sm:px-5 py-2.5 rounded-full hover:bg-[#B85C38] whitespace-nowrap shrink-0">
            Join the list
          </button>
          <button data-testid="nav-mobile-toggle" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu"
            className="xl:hidden inline-grid place-items-center w-10 h-10 text-[#2C2A28]">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <div className={`xl:hidden overflow-y-auto overscroll-contain transition-[max-height] duration-300 ease-out ${mobileOpen ? "max-h-[75vh] border-t hairline" : "max-h-0"}`}>
        <nav className="flex flex-col px-6 py-2 bg-[#F7F5F0]/95">
          {NAV.map(([label, id]) => (
            <button key={id} data-testid={`nav-m-${id}`} onClick={() => { scrollTo(id); setMobileOpen(false); }}
              className={`text-left py-3.5 border-b border-[#E4DFD3] last:border-0 font-mono-a text-xs uppercase tracking-[0.18em] ${active === id ? "text-[#B85C38]" : "text-[#595550]"}`}>
              {label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 160]);
  const scale = useTransform(scrollY, [0, 700], [1, 1.12]);
  const reduce = useReducedMotion();
  const lines = ["La dolce", "vita is", "ageless."];
  return (
    <section id="top" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <motion.img src={IMG.hero} alt="A private estate framed by jungle and water"
          animate={reduce ? {} : { scale: [1, 1.06, 1] }}
          transition={{ duration: 34, ease: "easeInOut", repeat: Infinity }}
          style={{ willChange: "transform" }}
          className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C2A28]/75 via-[#2C2A28]/25 to-[#2C2A28]/40" />
      </motion.div>
      <AtmosphericOverlay className="z-[1]" />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }}
        className="absolute top-20 sm:top-24 left-6 lg:left-12 z-10 max-w-[calc(100%-3rem)]">
        <span className="inline-block font-mono-a text-xs sm:text-sm uppercase tracking-[0.2em] text-white bg-[#2C2A28]/55 backdrop-blur-md px-4 py-2.5 rounded-2xl"
          style={{ textShadow: "0 1px 6px rgba(0,0,0,0.45)" }}>
          A women's vitality retreat · Florence &amp; Tuscany · April 25 – May 2, 2027
        </span>
      </motion.div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pb-16 lg:pb-24">
        <h1 className="font-serif text-[#F7F5F0] leading-[0.92] tracking-tighter text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
          {lines.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-[0.14em]">
              <motion.span custom={i} variants={heroLine} initial="hidden" animate="show" className="block">
                {i === 2 ? <span className="italic text-[#E7C6A5]">ageless.</span> : l}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1 }}
          className="mt-8 flex flex-col sm:flex-row sm:items-end gap-6 sm:justify-between">
          <p className="max-w-md text-[#F7F5F0]/85 text-lg leading-relaxed">
            Joy and vitality are a choice — seven nights in Florence &amp; Tuscany, where the science of aliveness meets the art of la dolce vita.
          </p>
          <div className="flex flex-col items-start gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="hero-reserve" onClick={() => scrollTo("reserve")}
                className="reserve-cta group inline-flex items-center gap-3 self-start bg-[#B85C38] text-[#F7F5F0] pl-6 pr-4 py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.18em]">
                Join the priority list
                <span className="grid place-items-center w-8 h-8 rounded-full bg-[#F7F5F0]/15 group-hover:bg-[#F7F5F0]/25">
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>
              <a href="/dolce-vita-audit" data-testid="hero-audit-cta"
                className="inline-flex items-center gap-2 self-start border border-[#F7F5F0]/40 text-[#F7F5F0] px-5 py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.18em] hover:bg-[#F7F5F0]/10 transition-colors">
                Take the Dolce Vita Audit
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
            <span className="pl-2 text-base text-[#F7F5F0]/75 italic">April 25 – May 2, 2027 — meanwhile, take the free 5-minute audit</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function AtmosphericOverlay({ className = "" }) {
  return <div aria-hidden="true" data-testid="atmospheric-overlay" className={`atmos-overlay ${className}`} />;
}

// Founders' letter + 4 ways to engage (Audit / Workshop / Curiosity Chat / Newsletter).
// Currently NOT rendered on the homepage — kept here to re-add later by placing <FoundersNote /> in the page.
// eslint-disable-next-line no-unused-vars
function FoundersNote() {
  const ways = [
    { icon: Sparkles, label: "Take the Dolce Vita Audit", href: "/dolce-vita-audit",
      d: "A free five-minute check to see how much sweetness you're already savoring — and where there's room for more.", testid: "founders-note-audit" },
    { icon: Users, label: "Come to a live workshop", href: "/workshop",
      d: "Our free live series on enhancing vitality and joy — a warm hour with Monica & Natalie.", testid: "founders-note-workshop" },
    { icon: MessageCircle, label: "Join a discovery call or Curiosity Chat", href: "/#connect",
      d: "A private call with Monica or Natalie, or one of our monthly group Curiosity Chats — come ask us anything.", testid: "founders-note-connect" },
    { icon: Mail, label: "Join Vita Nuova, our newsletter", href: "/#vita-nuova",
      d: "Slow pleasures, the science of vitality, and everyday beauty from Florence & Tuscany.", testid: "founders-note-newsletter" },
  ];
  return (
    <section id="founders-note" data-testid="founders-note" className="relative bg-[#EAE5D9] pt-16 pb-20 sm:pt-24 sm:pb-28 lg:pt-28 lg:pb-32 overflow-hidden">
      {/* soft blend easing down out of the hero */}
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#2C2A28]/15 to-transparent pointer-events-none" />
      <div className="relative max-w-[820px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="relative bg-[#F7F5F0] rounded-[4px] border hairline shadow-[0_28px_70px_-40px_rgba(44,42,40,0.5)] px-6 py-10 sm:px-12 sm:py-14 lg:px-16 lg:py-16">
            <div className="flex justify-center">
              <span className="grid place-items-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#B85C38]/10 text-[#B85C38] ring-1 ring-[#B85C38]/25">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
              </span>
            </div>
            <p className="mt-5 sm:mt-6 text-center kicker font-mono-a text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#B85C38]">A note from Monica &amp; Natalie</p>
            <h2 className="mt-3 sm:mt-4 text-center font-serif text-[2rem] leading-[1.08] sm:text-4xl lg:text-5xl tracking-tighter">
              We're giving this the <span className="italic">time it deserves.</span>
            </h2>

            <div className="mt-7 sm:mt-9 space-y-5 text-[#4a4642] text-[17px] sm:text-lg leading-[1.75] max-w-[46ch] mx-auto">
              <p>
                When we first dreamed this up, we imagined a harvest-season gathering. But so many of you
                told us the same thing — you need time to create space for such an immersive experience.
                We heard you.
              </p>
              <p>
                So we're moving the retreat to <span className="italic text-[#2C2A28]">Spring 2027 — April 25 to May 2</span>,
                when Tuscany is at its most alive. Peach, apricot and cherry orchards blossom in pinks and
                whites. Wisteria paths turn purple. Wild poppies begin to dot the fields — a full floral
                explosion, and the perfect backdrop for coming back to life.
              </p>
              <p>
                Until then, there are a few gentle ways to keep a little dolce vita energy flowing through
                your autumn and winter.
              </p>
            </div>

            <div className="mt-8 sm:mt-10 max-w-[46ch] mx-auto">
              <p className="text-[#4a4642] text-[17px] sm:text-lg italic">With warmth,</p>
              <p className="font-script text-[3.25rem] sm:text-6xl text-[#2C2A28] leading-[0.9] mt-1">Monica &amp; Natalie</p>
              <p className="mt-3 font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#9a938a]">Florence &middot; Tuscany</p>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 sm:mt-16">
          <p className="text-center font-mono-a text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#B85C38]">Ways to keep the sweetness flowing</p>
          <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ways.map(({ icon: Icon, label, href, d, testid }, i) => (
              <Reveal key={label} delay={0.06 * i}>
                <a href={href} data-testid={testid}
                  className="group flex flex-col h-full bg-[#F7F5F0] border hairline rounded-sm p-5 sm:p-6 hover:border-[#B85C38] hover:-translate-y-0.5 transition-all duration-300">
                  <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-[#B85C38]/10 text-[#B85C38]">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="mt-4 font-serif text-xl tracking-tight text-[#2C2A28] leading-snug">{label}</span>
                  <span className="mt-2 text-sm text-[#595550] leading-relaxed flex-1">{d}</span>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-mono-a text-[10px] uppercase tracking-[0.18em] text-[#B85C38] group-hover:gap-2.5 transition-all">
                    Explore <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ParallaxImg({ src, alt, heightClass = "" }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["-6%", "6%"]);
  return (
    <div ref={ref} className={`relative overflow-hidden rounded-sm ${heightClass}`}>
      <motion.img src={src} alt={alt} style={{ top: "-9%", y, willChange: "transform" }}
        className="absolute left-0 w-full h-[118%] object-cover" />
    </div>
  );
}

function SensoryMarquee() {
  const words = ["Olive", "Linen", "Espresso", "Cypress", "Laughter", "Terracotta", "Leather", "Silk", "Basil", "Golden hour"];
  return (
    <div data-testid="sensory-marquee" className="bg-[#2C2A28] py-5 overflow-hidden border-y border-[#F7F5F0]/10">
      <Marquee speed={26} gradient={false} autoFill>
        {words.map((w) => (
          <span key={w} className="mx-10 inline-flex items-center gap-10 font-mono-a text-sm uppercase tracking-[0.28em] text-[#E7C6A5]/85">
            {w}
            <span className="text-[#B85C38]" aria-hidden="true">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function TextMarquee() {
  return (
    <div className="py-10 border-y hairline bg-[#EAE5D9]">
      <Marquee speed={38} gradient={false}>
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} className="font-serif text-5xl lg:text-7xl tracking-tighter mx-8 flex items-center gap-8">
La dolce vita <span className="text-outline">·</span> Vitality is biology <span className="italic text-[#B85C38]">Aging is a choice</span> <span className="text-outline">·</span> Beauty as medicine
          </span>
        ))}
      </Marquee>
    </div>
  );
}

function Intention() {
  return (
    <section id="intention" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">The Intention</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.02]">
            Why we gather, and what we are <span className="italic">really</span> here to do.
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="relative mx-auto w-full max-w-[280px] sm:max-w-[320px]">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_renewal-hub-56/artifacts/bkrsn6uc_0D0A2959.jpg"
              alt="Monica holding a ceramic heart beside a driftwood heart"
              data-testid="intention-heart-photo"
              className="heart-photo w-full h-auto"
              loading="lazy"
            />
            <div aria-hidden="true" className="heart-photo-tint pointer-events-none absolute inset-0" />
          </div>
        </Reveal>
      </div>
      <div className="mt-20 lg:mt-28 space-y-20 lg:space-y-28">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.05}>
            <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-start border-t hairline pt-10">
              <div className="lg:col-span-3 font-serif text-7xl lg:text-8xl text-[#B85C38] tracking-tighter leading-none">{c.n}</div>
              <h3 className="lg:col-span-5 font-serif text-3xl lg:text-4xl tracking-tight leading-[1.08]">{c.title}</h3>
              <p className="lg:col-span-4 text-[#595550] text-lg leading-relaxed lg:mt-2">{c.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Transformation() {
  return (
    <section id="transformation" className="bg-[#2C2A28] text-[#F7F5F0] py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-7">
            <p className="font-mono-a text-[11px] uppercase tracking-[0.25em] text-[#E7C6A5] mb-6">The Transformation</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-4xl leading-[1.02]">
              You didn't come to be fixed. You came to <span className="italic text-[#E7C6A5]">feel alive again.</span>
            </h2>
            <p className="mt-8 text-[#F7F5F0]/75 text-lg lg:text-xl leading-relaxed max-w-2xl">
              Let's be honest about the real fear — the one underneath the creams, the procedures, the endless chasing. It isn't the lines on your face. It's the quiet dread that the aliveness is draining out of you, that the most vivid years are already behind you. That's the lie this week is built to put down. Not by fighting the years, but by flooding vitality back in — teaching your body to generate joy and aliveness from the inside, instead of managing a slow fade. Most women don't name the real shift until weeks later, back home: they just feel more alive — more awake, more like themselves — than they have in years, and this time it holds.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="overflow-hidden rounded-sm">
              <motion.img initial={{ scale: 1.1 }} whileHover={{ scale: 1.15 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                style={{ transformOrigin: "top" }}
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_renewal-hub-56/artifacts/x81k63um_0D0A2375.jpg"
                alt="A woman turning her face to the light" className="w-full h-[440px] lg:h-[600px] object-cover object-top" />
            </div>
          </Reveal>
        </div>

        <div className="mt-16 lg:mt-24 grid sm:grid-cols-2 gap-x-12 gap-y-12">
          {LAYERS.map((l, i) => (
            <Reveal key={l.n} delay={(i % 2) * 0.06}>
              <div className="border-t border-[#F7F5F0]/15 pt-6" data-testid={`layer-${i}`}>
                <div className="flex items-baseline gap-5">
                  <span className="font-serif text-4xl lg:text-5xl text-[#B85C38] tracking-tighter">{l.n}</span>
                  <h3 className="font-serif text-3xl lg:text-4xl tracking-tight">{l.t}</h3>
                </div>
                <p className="text-[#F7F5F0]/70 text-lg leading-relaxed mt-4">{l.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-16 lg:mt-24 text-[#F7F5F0]/85 text-lg lg:text-xl leading-relaxed max-w-3xl">
            Underneath all four is a single shift: from the woman quietly managing a decline to the <span className="italic text-[#E7C6A5]">Vital Matriarch</span> — not power over anyone, but power <span className="italic">with</span>; the woman whose own aliveness holds and lifts everyone around her. She's stopped trying to slow her age and started inhabiting it as a source of strength.
          </p>
        </Reveal>

        <div className="mt-20 lg:mt-28 grid lg:grid-cols-12 gap-8">
          <Reveal className="lg:col-span-4">
            <p className="font-mono-a text-[11px] uppercase tracking-[0.2em] text-[#E7C6A5]">You'll come home…</p>
          </Reveal>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10">
            {OUTCOMES.map((o, i) => (
              <Reveal key={i} delay={(i % 2) * 0.05}>
                <div className="flex gap-4 py-5 border-b border-[#F7F5F0]/12" data-testid={`outcome-${i}`}>
                  <ArrowUpRight className="w-5 h-5 shrink-0 mt-1 text-[#B85C38]" />
                  <p className="text-[#F7F5F0]/80 text-lg leading-relaxed">{o}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal>
          <p className="mt-20 font-serif italic text-2xl lg:text-4xl text-[#E7C6A5] max-w-3xl leading-tight tracking-tight">
            Spring in Tuscany is a slow awakening — and so is this: a gathering-in of the woman you have quietly become.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function WhatsCosting() {
  const COST = [
    { n: "01", t: "The slow fade", d: "You keep managing a decline nobody ever asked if you wanted. One morning the face in the mirror is a stranger, and you can't say when that happened." },
    { n: "02", t: "Always chasing the next fix", d: "You keep hunting for the thing that will finally make you feel beautiful and alive from the inside — the serum, the supplement, the new morning routine, the next expert, the trip that's meant to be different this time. And while you're chasing the fix that's always just out of reach, you can't taste what's already right in front of you: the people, the light, the ordinary pleasures on the table — the whole life that's still here, still trying to give itself to you." },
    { n: "03", t: "Going numb", d: "You get to the end of a beautiful day and realize you weren't really in it. Still successful, still showing up — but present at everything and there for none of it." },
    { n: "04", t: "It's not only you who pays", d: "This is costing the people closest to you, too. Your children, your partner, the ones who look to you to be the warm, steady center of things. When your joy is performed instead of real, they feel the gap — the resentment under the smile, the shame, the \u201cI'm fine\u201d that isn't. They can't name it and they don't know what to do with it, so they end up carrying it with you." },
    { n: "05", t: "The years only the brave ever spend", d: "Your 40s, 50s and 60s can be the richest years of your life — this is the season when a woman finally knows who she is, what she wants, and how connected she is to her own essence. Here's the quiet trap in carrying on as usual: you may never even feel the loss, because you'll never know what was possible. Only the women who get curious, who dare, ever find out. That's the real cost — not a pain you'll feel, but a whole life you'll never know you could have lived." },
  ];
  return (
    <section id="cost" className="bg-[#F7F5F0] py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">And if nothing changes?</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl">
            The quiet cost of <span className="italic">carrying on.</span>
          </h2>
          <p className="mt-8 text-[#595550] text-lg leading-relaxed max-w-xl">
            Probably nothing dramatic. That's what makes it so easy to keep putting off. The years just go on the way they've been going.
          </p>
        </Reveal>

        <div className="mt-20 lg:mt-28 grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="lg:sticky lg:top-28">
                <div className="relative aspect-[3/4] overflow-hidden rounded-sm">
                  <img src={IMG.contemplation} alt="A quiet moment, contemplating what comes next" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "50% 25%" }} />
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <div className="space-y-16 lg:space-y-20">
              {COST.map((c, i) => (
                <Reveal key={c.n} delay={i * 0.04}>
                  <div data-testid={`cost-${i}`} className="border-t hairline pt-8">
                    <div className="flex items-baseline gap-5">
                      <span className="font-serif text-5xl lg:text-6xl text-[#D8CDBE] tracking-tighter leading-none shrink-0">{c.n}</span>
                      <h3 className="font-serif text-2xl lg:text-4xl tracking-tight leading-[1.08]">{c.t}</h3>
                    </div>
                    <p className="mt-5 lg:pl-[4.75rem] text-[#595550] text-lg leading-relaxed max-w-xl">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        <Reveal>
          <p className="mt-24 font-serif italic text-xl sm:text-2xl lg:text-3xl text-[#2C2A28] max-w-3xl leading-snug tracking-tight">
            None of this is loud. That's what makes it so easy to keep postponing — and easy to reach the end still never knowing what you missed. This week is for the women who'd rather find out.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function WhatMakesDifferent() {
  return (
    <section id="different" className="bg-[#F7F5F0] pt-10 pb-20 lg:pt-16 lg:pb-28">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">What makes this different</p>
          <div className="space-y-6 text-[#595550] text-lg lg:text-xl leading-relaxed max-w-3xl">
            <p>Most retreats treat the pleasure as the reward — the beautiful week you've earned. Then you land back home and within a day or two it's gone, and you're the same tired woman who left, a little tanner and a little poorer.</p>
            <p>We do it the other way around. The beauty, the rest, the long meals, the pleasure — that's not the prize here, it's the method. It's what opens you enough for the real shift to happen. And because the week is only the middle — there's work before you arrive and after you get home — what opens in Tuscany is still with you in November, at your own kitchen table.</p>
          </div>
          <p className="mt-10 font-serif italic text-2xl sm:text-3xl lg:text-4xl text-[#2C2A28] max-w-3xl leading-tight tracking-tight">
            The pleasure isn't a break from the transformation. <span className="text-[#B85C38]">It's how the transformation happens.</span>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Location() {
  return (
    <section id="location" className="bg-[#EAE5D9] py-28 lg:py-40 border-y hairline">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-10 items-end mb-14">
          <Reveal>
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">The Location</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
              Florence, and the golden <span className="italic">hills</span> of Tuscany.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#595550] text-lg leading-relaxed lg:pb-2">
              A Renaissance city and the cypress-lined countryside beyond it — the food, the light, the art and the land, engaged not as sightseeing but as nervous-system medicine. Your home in Tuscany is shared with registered guests.
            </p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 auto-rows-[190px] lg:auto-rows-[230px]">
          <BentoImg src={IMG.loc1} className="col-span-2 lg:col-span-4 lg:row-span-2" alt="The boutique hotel lobby — checkered marble floor and a Florentine arcade mural" />
          <BentoImg src={IMG.landscape} className="col-span-2 lg:row-span-2" alt="A woman in a red dress before the olive groves and a Tuscan hilltop town" />
          <BentoImg src={IMG.shopping_friends} className="col-span-2 lg:col-span-3" alt="A woman with a red backpack in golden light before the Florence cathedral" />
          <BentoImg src={IMG.harvest} className="col-span-2" alt="A field of purple Tuscan iris in golden evening light" />
          <BentoImg src={IMG.piazza} className="col-span-2 lg:col-span-1" alt="Piazza della Signoria in Florence" />
          <BentoImg src={IMG.ponte_vecchio} className="col-span-2 lg:col-span-4 lg:row-span-2" alt="The Ponte Vecchio glowing over the Arno at golden hour" />
          <BentoImg src={IMG.loc3} className="col-span-2 lg:row-span-2" alt="A rustic stone village in Tuscany" />
          <BentoImg src={IMG.gathering} className="col-span-2 lg:col-span-3" alt="Women gathered at a candlelit Tuscan table, toasting together — above all, a gathering" />
          <BentoImg src={IMG.loc4} className="col-span-2" alt="A warm Tuscan garden in bloom" />
          <BentoImg src={IMG.street} className="col-span-2 lg:col-span-1" alt="A Florence street framing the cathedral dome" />
        </div>
      </div>
    </section>
  );
}

function BentoImg({ src, alt, className, imgClass = "" }) {
  return (
    <Reveal className={`${className} overflow-hidden rounded-sm`}>
      <motion.img whileHover={{ scale: 1.05 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        src={src} alt={alt} className={`w-full h-full object-cover ${imgClass}`} />
    </Reveal>
  );
}

function Attractions() {
  return (
    <section id="attractions" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <Reveal>
        <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">The Days</p>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.02]">
          Unhurried days — and by the end of them, <span className="italic">you're not the same.</span>
        </h2>
      </Reveal>
      <div className="mt-20 space-y-24 lg:space-y-32">
        {ATTRACTIONS.map((a, i) => (
          <Reveal key={a.k}>
            <div className={`grid lg:grid-cols-12 gap-8 lg:gap-14 items-center ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
              <div className="lg:col-span-7 [direction:ltr]">
                <ParallaxImg src={a.img} alt={a.k} heightClass="h-[340px] lg:h-[520px]" />
              </div>
              <div className="lg:col-span-5 [direction:ltr]">
                <div className="font-mono-a text-xs text-[#B85C38] tracking-[0.2em] mb-4">0{i + 1}</div>
                <h3 className="font-serif text-3xl lg:text-5xl tracking-tighter leading-[1.03]">{a.k}</h3>
                <p className="mt-5 text-[#595550] text-lg leading-relaxed max-w-md">{a.d}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section id="journey" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <Reveal className="lg:col-span-12">
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">The Journey</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.02]">
            An intimately crafted <span className="italic">transformation</span> journey.
          </h2>
          <p className="mt-6 text-[#595550] text-lg leading-relaxed max-w-2xl">{JOURNEY.intro}</p>
        </Reveal>
      </div>

      <div className="relative mt-16 lg:mt-24">
        <div className="hidden md:block absolute left-1/2 -translate-x-1/2 top-4 bottom-4 w-px bg-[#D4CEB8]" aria-hidden="true" />
        {JOURNEY.phases.map((p, i) => {
          const right = i % 2 === 1;
          return (
            <Reveal key={p.n}>
              <div className="relative md:grid md:grid-cols-2 md:items-center py-8 lg:py-14">
                <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 z-10 w-16 h-16 rounded-full bg-[#B85C38] text-[#F7F5F0] items-center justify-center font-serif text-2xl">
                  {p.n}
                </div>
                {right && <div className="hidden md:block" aria-hidden="true" />}
                <div className={right ? "md:pl-20" : "md:pr-20 md:text-right"} data-testid={`journey-${i}`}>
                  <p className="kicker font-mono-a text-xs uppercase tracking-[0.2em] text-[#B85C38]">
                    <span className="md:hidden">{p.n} · </span>{p.tag}
                  </p>
                  <h3 className="font-serif text-3xl lg:text-4xl tracking-tight mt-3">{p.title}</h3>
                  <p className={`text-[#595550] text-lg leading-relaxed mt-4 max-w-md ${right ? "" : "md:ml-auto"}`}>{p.body}</p>
                </div>
                {!right && <div className="hidden md:block" aria-hidden="true" />}
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function Includes() {
  return (
    <section id="includes" className="bg-[#EAE5D9] border-y hairline py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">What's Included</p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
                Every detail, <span className="italic">handled.</span>
              </h2>
              <p className="mt-6 text-[#595550] text-lg leading-relaxed max-w-md">{INCLUDES_INTRO.paras[0]}</p>
              <p className="mt-4 text-[#595550] text-lg leading-relaxed max-w-md">{INCLUDES_INTRO.paras[1]}</p>
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="space-y-12">
            {INCLUDE_GROUPS.map((g, gi) => (
              <Reveal key={g.group} delay={gi * 0.03}>
                <div data-testid={`include-group-${gi}`}>
                  <h3 className="font-serif text-2xl lg:text-3xl tracking-tight border-b border-[#B85C38]/30 pb-3">{g.group}</h3>
                  <p className="mt-2 font-serif italic text-[#8a837a] text-lg">{g.tagline}</p>
                  <div className="mt-5 space-y-5">
                    {g.items.map((item, i) => (
                      <div key={item.t} className="flex gap-4" data-testid={`include-${gi}-${i}`}>
                        <Check className="w-5 h-5 shrink-0 mt-1 text-[#B85C38]" />
                        <div>
                          <h4 className="font-serif text-xl tracking-tight leading-tight">
                            {item.t}
                            {item.optional && (
                              <span className="ml-2 align-middle font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#B85C38] border border-[#B85C38]/40 rounded-full px-2 py-0.5">
                                Optional
                              </span>
                            )}
                          </h4>
                          <p className="text-[#595550] leading-relaxed mt-1">{item.d}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-[#595550] leading-relaxed italic max-w-lg">{INCLUDES_INTRO.footer}</p>
        </div>
      </div>
    </section>
  );
}

function LivedNotListed() {
  return (
    <section id="lived" className="bg-[#F7F5F0] py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-4">Lived, not listed</p>
          <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.04] max-w-3xl">
            This is what your investment truly buys — a week of sensory adventures, <span className="italic">embodied.</span>
          </h3>
          <p className="mt-4 text-[#595550] leading-relaxed max-w-xl">Every frame below is Natalie, in Florence &amp; Tuscany — the light, the art, the markets, the hills. This is the exquisite puzzle of experiences chosen for you.</p>
        </Reveal>
        <FlipMosaic images={INCLUDES_GALLERY} />
      </div>
    </section>
  );
}

function FlipTile({ src, index }) {
  return (
    <div className="relative aspect-square overflow-hidden rounded-sm [perspective:900px]" data-testid={`included-photo-${index}`}>
      <AnimatePresence initial={false}>
        <motion.img
          key={src}
          src={src}
          alt="Natalie experiencing Florence & Tuscany"
          loading="lazy"
          initial={{ rotateY: -92, opacity: 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          exit={{ rotateY: 92, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden", willChange: "transform" }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
    </div>
  );
}

function FlipMosaic({ images }) {
  const reduce = useReducedMotion();
  const TILES = Math.min(images.length, 12);
  const [visible, setVisible] = useState(() => Array.from({ length: TILES }, (_, i) => i));
  const nextRef = useRef(TILES % images.length);

  useEffect(() => {
    if (reduce || images.length <= TILES) return;
    const id = setInterval(() => {
      setVisible((prev) => {
        const copy = [...prev];
        const pos = Math.floor(Math.random() * copy.length);
        if (!copy.includes(nextRef.current)) copy[pos] = nextRef.current;
        nextRef.current = (nextRef.current + 1) % images.length;
        return copy;
      });
    }, 2600);
    return () => clearInterval(id);
  }, [reduce, images.length, TILES]);

  return (
    <div className="mt-10 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="included-mosaic">
      {visible.map((imgIdx, pos) => (
        <FlipTile key={pos} src={images[imgIdx]} index={pos} />
      ))}
    </div>
  );
}

function GuidesCarousel() {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" }, [
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true }),
  ]);
  return (
    <div className="mt-16">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex gap-4">
          {GALLERY.map((src, i) => (
            <div key={i} className="min-w-0 shrink-0 grow-0 basis-[82%] sm:basis-[48%] lg:basis-[32%]">
              <div className="overflow-hidden rounded-sm">
                <img src={src} alt={`Natalie and Monica — a moment of aliveness (${i + 1})`}
                  className="w-full h-[320px] sm:h-[400px] lg:h-[460px] object-cover object-[center_14%]" data-testid={`guide-photo-${i}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-8">
        <button onClick={() => emblaApi && emblaApi.scrollPrev()} data-testid="guides-prev" aria-label="Previous photo"
          className="w-11 h-11 rounded-full border border-[#F7F5F0]/25 grid place-items-center hover:bg-[#F7F5F0]/10 text-[#F7F5F0]">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => emblaApi && emblaApi.scrollNext()} data-testid="guides-next" aria-label="Next photo"
          className="w-11 h-11 rounded-full border border-[#F7F5F0]/25 grid place-items-center hover:bg-[#F7F5F0]/10 text-[#F7F5F0]">
          <ChevronRight className="w-5 h-5" />
        </button>
        <span className="font-mono-a text-[10px] uppercase tracking-[0.2em] text-[#F7F5F0]/45 ml-2">Drag or swipe to explore</span>
      </div>
    </div>
  );
}

function QuoteBand({ quote, highlight, source }) {
  return (
    <section className="bg-[#2C2A28] text-[#F7F5F0] py-24 lg:py-36" data-testid="quote-band">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-[1.18] tracking-tight max-w-4xl">
            "{quote} <span className="text-[#E7C6A5]">{highlight}</span>"
          </p>
          <p className="mt-6 kicker font-mono-a text-xs uppercase tracking-[0.2em] text-[#B85C38]">{source}</p>
        </Reveal>
      </div>
    </section>
  );
}

function OurWhy() {
  return (
    <section id="story" className="bg-[#F7F5F0] py-28 lg:py-40" data-testid="our-why">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">Why this matters to us</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-4xl leading-[1.02]">
            We are not teaching this from a book. <span className="italic">We have lived it.</span>
          </h2>
          <p className="mt-6 text-[#595550] text-lg leading-relaxed max-w-2xl">
            Aging is inevitable. How we meet it is not. Left to the world's script it becomes something to fight, to hide, to apologise for. Met differently, it becomes the most liberating passage of a woman's life. For some the reckoning arrives quietly — a hum beneath the years. For others, it lands like thunder.
          </p>
        </Reveal>

        <Reveal>
          <blockquote className="my-20 lg:my-28 max-w-4xl">
            <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-[#2C2A28]">
              "Joy is a prerequisite, <span className="text-[#B85C38]">not an outcome.</span>"
            </p>
            <footer className="mt-5 font-mono-a text-[11px] uppercase tracking-[0.2em] text-[#595550]">Monica · The Joyful Rebellion</footer>
          </blockquote>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center" data-testid="monica-story">
          <Reveal className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-sm">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_renewal-hub-56/artifacts/nfhvio31_0D0A2899.jpg"
                alt="Monica, guide and founder"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.22em] text-[#B85C38] mb-4">Monica's story</p>
            <div className="space-y-5 text-[#595550] text-lg leading-relaxed">
              <p>For years it was a hum in the background. Then perimenopause turned it into a spotlight — the anxiety, the fear that I'd never again taste life as ferociously as I once had, a body changing in ways that made me question my worth: whether I was still someone who could be desired, still someone who could be loved.</p>
              <p>My aliveness halved. And still I kept going — building the business, holding everyone I loved, tending the earth. Until I walked into the darkness and understood I had to reset entirely: to nourish myself, to listen to my body instead of overriding it, to stop pouring my life force into a wounded ego's demands — proving, pleasing, waiting to be perfect before I dared.</p>
              <p className="text-[#2C2A28]">I learned that joy is non-negotiable. That pleasure and meaning are not the reward for a life well lived — they are the fuel for it. And that only from that wholeness could I become what I was always here to be: a stand for other women's transformation.</p>
              <p>This season of reinvention was a precious gift — it let me discover and choose the practices that regenerate me, reconnecting with my true inner nature and with everything around me. And why Italy? Because it is where my heart feels at home. Here, connecting with beauty in all its forms — art, food, sound, texture, landscape, open-hearted people — comes naturally. La dolce vita is always present: not as false positivity, but as a way of turning toward what gives us life, satisfaction and meaning.</p>
            </div>
          </Reveal>
        </div>

        <Reveal>
          <blockquote className="mt-20 lg:mt-28 max-w-3xl">
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-[1.2] tracking-tight text-[#2C2A28]">
              "The joyful rebellion isn't grand. It's the quiet, daily, countercultural act of letting yourself <span className="text-[#B85C38]">arrive</span> — in your body, in this moment, in the pleasure already here."
            </p>
            <footer className="mt-5 font-mono-a text-[11px] uppercase tracking-[0.2em] text-[#595550]">Monica · The Joyful Rebellion</footer>
          </blockquote>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mt-20 lg:mt-28" data-testid="natalie-story">
          <Reveal className="lg:col-span-7 order-2 lg:order-1">
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.22em] text-[#B85C38] mb-4">Natalie's story</p>
            <div className="space-y-5 text-[#595550] text-lg leading-relaxed">
              <p>My first journey to Florence was not one I chose — it was chosen for me. My only request to my best friend was simple: <span className="italic">take me somewhere beautiful</span>. He delivered. We travelled the way we always did — fitting two weeks of exploring into five whirlwind days — and Florence answered by awakening every sense and, more than that, reigniting my soul. I left knowing, with my whole body, that I had to return.</p>
              <p>Three months later I did — for a sabbatical. I joined a friend at art school, studied Renaissance art history, took private painting lessons. One month became two as a single open door became many, each drawing me toward a life that was simpler, more joyful, beautiful in every ordinary way. In the years since I've brought my family to explore Italy end to end — Venice to Sicily, mountains to lakes, rolling hills to medieval villages that hold their charm in perpetuity.</p>
              <p>Italy has a way of adopting you. All it asks is a little courage — to fumble through the language, to share the table, to try without shame for your imperfections. Every effort is met with warmth, and every effort opens another door. I've returned every year since, and each time the same magical energy meets me — through every transition and its most painful moments, Italy has held me in a way that is intangible and utterly visceral. And each time I've come away more myself, not less, than the year before.</p>
              <p className="text-[#2C2A28]">The people, the art, the architecture, the food, the fields of flowers — Italy wrapped itself around me until beauty stopped being something I visited and became something I lived. It changed how I inhabit my own life: slower, more awake, far less braced against time. That is why I bring women here now — not to sightsee, but to feel that same shift in themselves. Italy has a way of returning you to who you were before the world told you to hurry.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative overflow-hidden rounded-sm">
              <img
                src="/natalie-story.webp"
                alt="Natalie, guide and founder"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>

        <Reveal>
          <blockquote className="mt-20 lg:mt-28 max-w-3xl">
            <p className="font-serif italic text-2xl sm:text-3xl lg:text-4xl leading-[1.2] tracking-tight text-[#2C2A28]">
              "Italy submerged me in a beauty so complete it became <span className="text-[#B85C38]">timeless</span> — an invitation to rewire your mind and rewrite the story of your life."
            </p>
            <footer className="mt-5 font-mono-a text-[11px] uppercase tracking-[0.2em] text-[#595550]">Natalie · La Dolce Vita</footer>
          </blockquote>
        </Reveal>
      </div>
    </section>
  );
}

function Team() {
  return (
    <section id="team" className="bg-[#2C2A28] text-[#F7F5F0] py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <p className="font-mono-a text-[11px] uppercase tracking-[0.25em] text-[#E7C6A5] mb-6">Your Guides</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl leading-[1.02]">
            Designed & led by two women who <span className="italic">embody</span> what they teach.
          </h2>
        </Reveal>

        {/* Aliveness carousel — real moments between Natalie & Monica */}
        <GuidesCarousel />
        <Reveal>
          <p className="mt-8 font-serif italic text-2xl lg:text-3xl text-[#E7C6A5] max-w-2xl leading-tight">
            "The magic is in the aliveness between us — that's what we bring to Tuscany."
          </p>
          <p className="mt-6 text-[#F7F5F0]/70 text-lg leading-relaxed max-w-2xl">
            We treat transformation work and living fully with the same passion, joy and commitment. Join us for the experience of your lifetime.
          </p>
        </Reveal>

        <div className="mt-20 grid md:grid-cols-2 gap-12 lg:gap-16">
          {TEAM.map((m, i) => (
            <Reveal key={m.name} delay={i * 0.08}>
              <h3 className="font-serif text-3xl lg:text-4xl tracking-tight">{m.name}</h3>
              <p className="font-mono-a text-[11px] uppercase tracking-[0.16em] text-[#E7C6A5] mt-2">{m.role}</p>
              <p className="text-[#F7F5F0]/70 leading-relaxed mt-5 text-lg">{m.bio}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function emphasize(quote, phrase) {
  if (!phrase) return quote;
  const i = quote.indexOf(phrase);
  if (i === -1) return quote;
  return (
    <>
      {quote.slice(0, i)}
      <span className="text-[#B85C38] font-semibold">{phrase}</span>
      {quote.slice(i + phrase.length)}
    </>
  );
}

function OliveDivider() {
  return (
    <div className="flex justify-center my-8" aria-hidden="true">
      <svg width="240" height="24" viewBox="0 0 240 24" fill="none" className="text-[#8a9575]">
        <path d="M14 12 H104" stroke="currentColor" strokeWidth="1" />
        <path d="M226 12 H136" stroke="currentColor" strokeWidth="1" />
        <g fill="currentColor" opacity="0.9">
          <ellipse cx="40" cy="6" rx="7" ry="3" transform="rotate(-28 40 6)" />
          <ellipse cx="60" cy="18" rx="7" ry="3" transform="rotate(28 60 18)" />
          <ellipse cx="80" cy="6" rx="7" ry="3" transform="rotate(-28 80 6)" />
          <ellipse cx="200" cy="6" rx="7" ry="3" transform="rotate(28 200 6)" />
          <ellipse cx="180" cy="18" rx="7" ry="3" transform="rotate(-28 180 18)" />
          <ellipse cx="160" cy="6" rx="7" ry="3" transform="rotate(28 160 6)" />
        </g>
        <rect x="115" y="7" width="10" height="10" transform="rotate(45 120 12)" fill="#B85C38" />
      </svg>
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <figure className="mx-3 w-[320px] sm:w-[380px] shrink-0 bg-[#F7F5F0] border hairline rounded-sm p-7 whitespace-normal" data-testid="testimonial-card">
      <span aria-hidden="true" className="font-serif text-4xl leading-none text-[#B85C38]/50">&ldquo;</span>
      <blockquote className="mt-1 font-serif italic text-lg lg:text-xl leading-[1.4] tracking-tight text-[#2C2A28]">
        {emphasize(t.quote, t.emphasis)}
      </blockquote>
      <figcaption className="mt-5 pt-4 border-t hairline">
        <span className="block kicker font-mono-a text-xs uppercase tracking-[0.18em] text-[#B85C38]">{t.name || "A client"}</span>
        {t.role && <span className="block mt-1 text-sm text-[#595550]">{t.role}</span>}
      </figcaption>
    </figure>
  );
}

function Testimonials() {
  const mid = Math.ceil(TESTIMONIALS.length / 2);
  const rowA = TESTIMONIALS.slice(0, mid);
  const rowB = TESTIMONIALS.slice(mid);
  return (
    <section id="voices" className="bg-[#EAE5D9] py-28 lg:py-40 overflow-hidden" data-testid="testimonials">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6" data-testid="testimonials-kicker">From the women we've guided over the years</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            A mirror, held with <span className="italic">bold love.</span>
          </h2>
          <OliveDivider />
          <p className="text-[#595550] text-lg leading-relaxed max-w-2xl mx-auto">
            The women who've walked this path with Natalie &amp; Monica in their years of coaching and guiding — in their own words.
          </p>
        </Reveal>
      </div>
      <div className="mt-14 space-y-5">
        <Marquee speed={32} gradient gradientColor="#EAE5D9" gradientWidth={90} pauseOnHover>
          {rowA.map((t, i) => <TestimonialCard key={i} t={t} />)}
        </Marquee>
        <Marquee speed={30} direction="right" gradient gradientColor="#EAE5D9" gradientWidth={90} pauseOnHover>
          {rowB.map((t, i) => <TestimonialCard key={i} t={t} />)}
        </Marquee>
      </div>
    </section>
  );
}

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <div className="grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4">
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">Questions</p>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter leading-[1.02]">Everything you might be wondering.</h2>
        </Reveal>
        <div className="lg:col-span-8">
          {FAQS.map((f, i) => (
            <div key={i} className="border-b hairline">
              <button data-testid={`faq-${i}`} onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-start justify-between gap-6 py-7 text-left group">
                <span className="font-serif text-2xl lg:text-3xl tracking-tight group-hover:text-[#B85C38]">{f.q}</span>
                <Plus className={`w-6 h-6 shrink-0 mt-1 text-[#B85C38] transition-transform duration-500 ${open === i ? "rotate-45" : ""}`} />
              </button>
              <motion.div initial={false} animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
                <p className={`text-[#595550] text-lg leading-relaxed max-w-2xl ${f.steps ? "pb-3" : "pb-8"}`}>{f.a}</p>
                {f.steps && (
                  <ul className="pb-3 max-w-2xl space-y-3">
                    {f.steps.map((s, idx) => (
                      <li key={idx} className="flex items-baseline gap-3 text-[#595550] text-lg leading-relaxed">
                        <span className="font-mono-a text-[11px] tracking-[0.16em] text-[#B85C38] shrink-0">0{idx + 1}</span>
                        {typeof s === "string" ? <span>{s}</span> : <span><span className="text-[#2C2A28] font-medium">{s.t}</span> {s.d}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {f.after && <p className="pb-8 max-w-2xl text-[#595550] text-lg leading-relaxed italic">{f.after}</p>}
              </motion.div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Discovery Call still uses the Funnel Gorgeous scheduler (30-min 1:1).
// Curiosity Chat sessions are now served by our own backend: /api/curiosity/sessions
export const DISCOVERY_LINK = "https://link.fgfunnels.com/widget/bookings/awrdc";

function BookingModal({ booking, onClose }) {
  useEffect(() => {
    if (!booking) return;
    // Load Funnel Gorgeous / LeadConnector embed script once for iframe auto-resize.
    const SRC = "https://link.fgfunnels.com/js/form_embed.js";
    if (!document.querySelector(`script[src="${SRC}"]`)) {
      const s = document.createElement("script");
      s.src = SRC;
      s.async = true;
      document.body.appendChild(s);
    }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [booking, onClose]);

  return (
    <AnimatePresence>
      {booking && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }} data-testid="booking-modal">
          <div className="absolute inset-0 bg-[#2C2A28]/70 backdrop-blur-md" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-2xl bg-[#F7F5F0] rounded-sm shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            initial={{ opacity: 0, y: 28, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-center justify-between gap-4 px-6 py-4 border-b hairline bg-[#EAE5D9]">
              <div>
                <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">Reserve your place</p>
                <h3 className="font-serif text-xl tracking-tight text-[#2C2A28] mt-0.5">{booking.label}</h3>
              </div>
              <button onClick={onClose} data-testid="booking-modal-close" aria-label="Close booking"
                className="w-10 h-10 shrink-0 rounded-full border border-[#D4CEB8] grid place-items-center text-[#595550] hover:border-[#B85C38] hover:text-[#B85C38] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain bg-[#F7F5F0]" style={{ WebkitOverflowScrolling: "touch" }}>
              <iframe
                src={booking.url}
                title={`Book — ${booking.label}`}
                className="w-full h-[70vh] min-h-[70vh] border-0 block"
                scrolling="yes"
                allow="payment"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CuriosityBookingModal({ session, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [nlState, setNlState] = useState("idle");
  const subscribeNl = async () => {
    if (nlState !== "idle") return;
    setNlState("loading");
    try {
      const res = await fetch(`${API}/newsletter`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), name: form.name.trim(), source: "curiosity-thankyou" }),
      });
      if (!res.ok) throw new Error();
      setNlState("done");
    } catch (e) { setNlState("idle"); toast.error("Couldn't subscribe — please try again."); }
  };

  useEffect(() => {
    if (!session) { setForm({ name: "", email: "", phone: "", consent: false }); setDone(false); return; }
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [session, onClose]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Please add your first name."); return; }
    if (!form.email.trim()) { toast.error("Please add your email."); return; }
    if (!form.phone.trim()) { toast.error("Please add your phone number."); return; }
    if (!form.consent) { toast.error("Please tick the box so we can send your Zoom link."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/curiosity/book`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, session_id: session.id, origin: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch (e) {
      toast.error("Something went wrong — please try again.");
    } finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {session && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }} data-testid="curiosity-modal">
          <div className="absolute inset-0 bg-[#2C2A28]/70 backdrop-blur-md" onClick={onClose} />
          <motion.div className="relative w-full max-w-md bg-[#F7F5F0] rounded-sm shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            initial={{ opacity: 0, y: 28, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b hairline bg-[#EAE5D9]">
              <div>
                <p className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">Curiosity Chat</p>
                <h3 className="font-serif text-lg tracking-tight text-[#2C2A28] mt-0.5">{session.label}</h3>
                <p className="text-[#7a746c] text-xs mt-0.5">{session.sub}</p>
              </div>
              <button onClick={onClose} data-testid="curiosity-modal-close" aria-label="Close booking"
                className="w-10 h-10 shrink-0 rounded-full border border-[#D4CEB8] grid place-items-center text-[#595550] hover:border-[#B85C38] hover:text-[#B85C38] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-6" style={{ WebkitOverflowScrolling: "touch" }}>
              {done ? (
                <div className="text-center py-8" data-testid="curiosity-success">
                  <div className="w-14 h-14 rounded-full bg-[#5C6B50] grid place-items-center mx-auto"><Check className="w-7 h-7 text-[#F7F5F0]" /></div>
                  <h3 className="font-serif text-2xl tracking-tight mt-5">You're in. Thank you for your curiosity.</h3>
                  <p className="text-[#595550] mt-3 leading-relaxed text-sm" data-testid="curiosity-success-msg">Your confirmation email with the details on how to join<br /><span className="text-[#2C2A28]">{session.label}</span><br />is on its way — check your inbox, and peek into your spam folder if you don't see it.</p>
                  <p className="text-[#595550] mt-3 leading-relaxed text-sm">Please add <span className="text-[#B85C38]">hello@agelesswomanretreat.com</span> to your contacts so you don't miss important communications about this event and the upcoming Ageless Woman retreat.</p>
                  {nlState === "done" ? (
                    <p data-testid="curiosity-newsletter-done" className="mt-4 text-sm text-[#5C6B50]">✓ You're on the Vita Nuova list — a first taste of Tuscany is on its way.</p>
                  ) : (
                    <label className="mt-4 flex items-start gap-2.5 cursor-pointer text-left max-w-md mx-auto">
                      <input type="checkbox" data-testid="curiosity-newsletter-optin" checked={false} disabled={nlState === "loading"}
                        onChange={subscribeNl} className="mt-0.5 accent-[#B85C38] cursor-pointer" />
                      <span className="text-sm text-[#595550] leading-relaxed">
                        {nlState === "loading" ? "Adding you to the list…" : <>Also send me <em className="text-[#2C2A28]">Vita Nuova</em> — our newsletter of slow pleasures, the science of vitality, and everyday beauty from Tuscany.</>}
                      </span>
                    </label>
                  )}
                  <button onClick={onClose} data-testid="curiosity-done" className="mt-6 reserve-cta inline-flex bg-[#2C2A28] text-[#F7F5F0] px-8 py-3 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#B85C38]">Lovely</button>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-[#595550] text-sm leading-relaxed">Pop in your details and we'll email your Zoom link right away, plus reminders before we start.</p>
                  <input data-testid="curiosity-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="First name" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
                  <input data-testid="curiosity-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Email address" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
                  <input data-testid="curiosity-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="Phone / WhatsApp (for reminders)" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
                  <label className="flex items-start gap-3 text-sm text-[#595550] cursor-pointer">
                    <input type="checkbox" data-testid="curiosity-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                      className="mt-1 accent-[#B85C38]" />
                    <span>Yes, email &amp; text me my Zoom link and reminders for this session.</span>
                  </label>
                  <button data-testid="curiosity-submit" onClick={submit} disabled={loading}
                    className="reserve-cta w-full bg-[#B85C38] text-[#F7F5F0] py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#2C2A28] disabled:opacity-60">
                    {loading ? "Booking…" : "Reserve my spot"}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Connect() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "chats", message: "", sessions: [] });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [booking, setBooking] = useState(null);
  const [curiositySessions, setCuriositySessions] = useState([]);
  const [curiositySession, setCuriositySession] = useState(null);

  useEffect(() => {
    fetch(`${API}/curiosity/sessions`).then((r) => r.json())
      .then((d) => setCuriositySessions(d.sessions || [])).catch(() => {});
  }, []);

  const openBooking = (url, label) => {
    if (!url) { toast.info("Booking link coming soon."); return; }
    setBooking({ url, label });
  };

  const submit = async () => {
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, origin: window.location.origin }),
      });
      if (!res.ok) throw new Error();
      setSent(true);
    } catch (e) {
      toast.error("Something went wrong — please try again.");
    } finally { setLoading(false); }
  };

  return (
    <section id="connect" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">Begin the Conversation</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Not sure this retreat in spring 2027 is <span className="italic">for you?</span>
          </h2>
          <p className="mt-6 text-[#595550] text-lg leading-relaxed max-w-md">
            We honor and welcome deep inquiry for such an important choice — let's explore it together. Your inner timing is essential; only when it <span className="italic">feels right</span> can you take in all the gifts this retreat has to offer. Book a private discovery call, or join one of our open-forum Curiosity Chats.
          </p>
          <div className="mt-8 flex items-center gap-6 max-w-md">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_renewal-hub-56/artifacts/ojv39lzk_4a55f3ec-2ad0-413e-be9e-8882a2e113ef.png"
              alt="Natalie and Monica smiling together"
              data-testid="connect-guides-photo"
              className="w-32 sm:w-36 shrink-0 h-auto"
              loading="lazy"
            />
            <div className="space-y-4">
              <div className="flex gap-4"><ArrowUpRight className="w-5 h-5 mt-1 shrink-0 text-[#B85C38]" /><p className="text-[#595550]"><span className="text-[#2C2A28] font-medium">A discovery call</span> — a warm 30-minute 1:1 call to see if this is your year.</p></div>
              <div className="flex gap-4"><ArrowUpRight className="w-5 h-5 mt-1 shrink-0 text-[#B85C38]" /><p className="text-[#595550]"><span className="text-[#2C2A28] font-medium">A Curiosity Chat</span> — an open, no-pressure 45-minute live group session with Natalie &amp; Monica. Pick a date and book in seconds.</p></div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-[#EAE5D9] border hairline rounded-sm p-8 lg:p-10">
            {sent ? (
              <div className="text-center py-10" data-testid="connect-success">
                <div className="w-14 h-14 rounded-full bg-[#5C6B50] grid place-items-center mx-auto"><Check className="w-7 h-7 text-[#F7F5F0]" /></div>
                <h3 className="font-serif text-3xl tracking-tight mt-6">Thank you — it's on its way.</h3>
                <p className="text-[#595550] mt-3 leading-relaxed">We'll personally be in touch within 48 hours. Keep an eye on your inbox.</p>
              </div>
            ) : (
              <>
                <div className="flex gap-2 mb-6">
                  {[["chats", "Curiosity Chats"], ["call", "Discovery call"]].map(([v, label]) => (
                    <button key={v} type="button" data-testid={`connect-intent-${v}`} onClick={() => { setForm({ ...form, interest: v }); setShowForm(false); }}
                      className={`flex-1 py-3 rounded-full font-mono-a text-[11px] uppercase tracking-[0.12em] border transition-colors ${form.interest === v ? "bg-[#2C2A28] text-[#F7F5F0] border-[#2C2A28]" : "border-[#D4CEB8] text-[#595550] hover:border-[#B85C38]"}`}>
                      {label}
                    </button>
                  ))}
                </div>

                {form.interest === "chats" ? (
                  <div>
                    <p className="font-mono-a text-[11px] uppercase tracking-[0.16em] text-[#595550] mb-3">Pick a session — you'll get the Zoom link &amp; reminders instantly</p>
                    <div className="space-y-2">
                      {curiositySessions.filter((s) => !s.past).map((s, i) => (
                        <button key={s.id} type="button" data-testid={`chat-book-${i}`}
                          onClick={() => setCuriositySession(s)}
                          className="group w-full flex items-center justify-between gap-3 px-4 py-4 rounded-sm border border-[#D4CEB8] hover:border-[#B85C38] hover:bg-[#F7F5F0] transition-colors text-left">
                          <span>
                            <span className="block text-[#2C2A28] text-sm">{s.label}</span>
                            <span className="block text-[#7a746c] text-xs mt-0.5">{s.sub}</span>
                          </span>
                          <ArrowUpRight className="w-4 h-4 shrink-0 text-[#B85C38] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                      ))}
                      {curiositySessions.filter((s) => !s.past).length === 0 && (
                        <p className="text-[#7a746c] text-sm py-4">New dates coming soon — use the email option below and we'll let you know.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="font-mono-a text-[11px] uppercase tracking-[0.16em] text-[#595550] mb-3">A warm 30-minute 1:1 — book a time that suits you</p>
                    <button type="button" data-testid="discovery-book"
                      onClick={() => openBooking(DISCOVERY_LINK, "Discovery Call · 30-minute 1:1")}
                      className={`reserve-cta w-full flex items-center justify-center gap-2 py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] transition-colors ${DISCOVERY_LINK ? "bg-[#B85C38] text-[#F7F5F0] hover:bg-[#2C2A28]" : "bg-[#B85C38]/60 text-[#F7F5F0] cursor-not-allowed"}`}>
                      Book my discovery call <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <button type="button" data-testid="connect-email-toggle" onClick={() => setShowForm((v) => !v)}
                  className="mt-6 text-[#595550] text-sm underline underline-offset-4 hover:text-[#B85C38] transition-colors">
                  {showForm ? "Hide email option" : "Prefer to email us instead?"}
                </button>

                {showForm && (
                  <div className="mt-4 space-y-4">
                    <input data-testid="connect-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Full name" className="w-full bg-[#F7F5F0] border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
                    <input data-testid="connect-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email address" className="w-full bg-[#F7F5F0] border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
                    <input data-testid="connect-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="Phone / WhatsApp (optional)" className="w-full bg-[#F7F5F0] border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38]" />
                    <textarea data-testid="connect-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder="Anything you'd like us to know? (optional)" rows={3} className="w-full bg-[#F7F5F0] border hairline rounded-sm px-4 py-3 outline-none focus:border-[#B85C38] resize-none" />
                    <button data-testid="connect-submit" onClick={submit} disabled={loading}
                      className="reserve-cta w-full bg-[#2C2A28] text-[#F7F5F0] py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#B85C38] disabled:opacity-60">
                      {loading ? "Sending…" : "Send us a message"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </Reveal>
      </div>
      <BookingModal booking={booking} onClose={() => setBooking(null)} />
      <CuriosityBookingModal session={curiositySession} onClose={() => setCuriositySession(null)} />
    </section>
  );
}

function SeatsNote({ className = "" }) {
  const [seats, setSeats] = useState(null);
  useEffect(() => {
    let on = true;
    fetch(`${API}/seats`).then((r) => r.json()).then((d) => { if (on) setSeats(d); }).catch(() => {});
    return () => { on = false; };
  }, []);
  if (!seats || seats.remaining <= 0) return null;
  return (
    <p data-testid="seats-note" className={className}>
      <span className="text-[#B85C38]">{seats.remaining}</span> of {seats.total} places remaining
    </p>
  );
}

function Reserve() {
  return (
    <section id="reserve" className="bg-[#EAE5D9] border-t hairline py-28 lg:py-40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38] mb-6">The Priority List</p>
          <h2 className="font-serif text-5xl lg:text-7xl tracking-tighter leading-[0.98]">
            The doors open <span className="italic">this fall.</span>
          </h2>
          <p className="mt-6 text-[#595550] text-lg leading-relaxed max-w-md">
            The retreat takes place in Florence and Tuscany, <span className="italic">April 25 – May 2, 2027</span>. Applications open this fall — and because the circle is small, the priority list is invited first. There's nothing to pay now; simply add your name and you'll be the first we reach out to when the doors open.
          </p>
          <ul className="mt-8 space-y-3">
            {["The full details before they go public", "First invitation to apply when the doors open", "Find out first about new experiences we're adding to the spring retreat — plus a surprise bonus for the women committed to their reinvention and aliveness"].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[#595550]"><ArrowUpRight className="w-4 h-4 mt-1 shrink-0 text-[#B85C38]" /> {t}</li>
            ))}
          </ul>
          <button data-testid="reserve-book-call" onClick={() => scrollTo("connect")}
            className="mt-8 font-mono-a text-[11px] uppercase tracking-[0.16em] text-[#2C2A28] border-b border-[#B85C38] pb-1 hover:text-[#B85C38]">
            Want to talk it through first? Book a discovery call →
          </button>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="bg-[#F7F5F0] border hairline rounded-sm p-8 lg:p-12 text-center">
            <p className="kicker font-mono-a text-xs uppercase tracking-[0.2em] text-[#B85C38]">Step one</p>
            <h3 className="mt-4 font-serif text-3xl lg:text-4xl tracking-tight leading-tight">
              Join the priority list
            </h3>
            <p className="mt-5 text-[#595550] leading-relaxed">
              Add your name and be first through the door. No payment, no application yet — just the very first word when applications for the retreat open this fall.
            </p>
            <a href="/priority" data-testid="reserve-apply-button"
              className="reserve-cta mt-8 inline-flex items-center justify-center gap-2 w-full bg-[#2C2A28] text-[#F7F5F0] py-4 rounded-full font-mono-a text-xs uppercase tracking-[0.2em] hover:bg-[#B85C38]">
              Claim my place on the list <ArrowUpRight className="w-4 h-4" />
            </a>
            <p className="mt-4 text-xs text-[#9a938a] italic leading-relaxed">
              A small circle. The list is invited before anyone else.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function IncludedChecklist() {
  const [open, setOpen] = useState(false);
  return (
    <section id="included-checklist" className="bg-[#F7F5F0] pb-20 lg:pb-28">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <Reveal>
          <button data-testid="included-checklist-toggle" onClick={() => setOpen((o) => !o)}
            className={`w-full flex items-center justify-between gap-4 border hairline bg-[#EAE5D9] px-6 lg:px-10 py-6 lg:py-8 text-left transition-colors hover:border-[#B85C38] ${open ? "rounded-t-sm" : "rounded-sm"}`}>
            <div>
              <p className="kicker font-mono-a text-xs uppercase tracking-[0.25em] text-[#B85C38]">One last look</p>
              <h2 className="mt-2 font-serif text-3xl lg:text-4xl tracking-tight">Everything that's included</h2>
              <p className="mt-2 text-[#595550] text-sm">The full checklist — every detail already handled for you.</p>
            </div>
            <ChevronDown className={`w-6 h-6 shrink-0 text-[#B85C38] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                <div data-testid="included-checklist-list" className="border hairline border-t-0 rounded-b-sm bg-[#EAE5D9]/60 px-6 lg:px-10 py-8 lg:py-10">
                  <ul className="grid sm:grid-cols-2 gap-x-12 gap-y-4">
                    {INCLUDED_CHECKLIST.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-[#595550] leading-relaxed">
                        <span className="mt-1.5 w-4 h-4 rounded-full bg-[#B85C38]/12 grid place-items-center shrink-0">
                          <Check className="w-2.5 h-2.5 text-[#B85C38]" strokeWidth={3} />
                        </span>
                        <span className="text-[15px]">{t}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-8 pt-5 border-t hairline text-xs text-[#9a938a] italic">
                    Not included: flights to Italy and airport transfers to and from the hotel in Florence.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Please add a valid email."); return; }
    setSubLoading(true);
    try {
      const nl = new URLSearchParams(window.location.search).get("nl");
      const source = nl ? `${nl}-email` : "footer";
      const res = await fetch(`${API}/newsletter`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), source }),
      });
      if (!res.ok) throw new Error();
      setSubscribed(true);
    } catch (err) {
      toast.error("Couldn't subscribe. Please try again.");
    } finally { setSubLoading(false); }
  };
  return (
    <>
    <a href="/priority" data-testid="home-priority-banner"
      className="block bg-[#EAE5D9] border-y hairline hover:bg-[#E4DECB] transition-colors">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-3.5 flex items-center justify-center gap-2.5 text-center flex-wrap">
        <span className="font-mono-a text-[10px] uppercase tracking-[0.22em] text-[#B85C38]">April 25 – May 2, 2027 · Applications open this fall</span>
        <span className="hidden sm:inline text-[#c9c1b3]">·</span>
        <span className="text-sm text-[#595550]">Join the priority list before the doors open</span>
        <span className="text-[#B85C38]" aria-hidden="true">→</span>
      </div>
    </a>
    <footer className="bg-[#2C2A28] text-[#F7F5F0] py-16">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex flex-col md:flex-row justify-between gap-10">
        <div>
          <img src="/images/logo-mark.png" alt="The Ageless Woman mark" className="h-16 w-16 object-contain brightness-0 invert opacity-90" />
          <div className="font-serif text-3xl tracking-tight mt-3">The Ageless Woman</div>
          <p className="font-mono-a text-[11px] uppercase tracking-[0.18em] text-[#F7F5F0]/50 mt-3">agelesswomanretreat.com</p>
        </div>
        <div id="vita-nuova" className="max-w-sm">
          <div className="font-mono-a text-[11px] uppercase tracking-[0.2em] text-[#E7C6A5]">Vita Nuova — our newsletter</div>
          <p className="text-sm text-[#F7F5F0]/60 mt-2 leading-relaxed">Slow pleasures, the science of vitality, and everyday beauty from Florence &amp; Tuscany. A note now and then — never noise.</p>
          {subscribed ? (
            <p data-testid="newsletter-success" className="mt-4 text-sm text-[#E7C6A5]">You're in — a little welcome is on its way to your inbox.</p>
          ) : (
            <form onSubmit={subscribe} className="mt-4 flex gap-2">
              <input data-testid="newsletter-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 min-w-0 bg-transparent border border-[#F7F5F0]/20 rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#E7C6A5] placeholder:text-[#F7F5F0]/30" />
              <button data-testid="newsletter-subscribe" type="submit" disabled={subLoading}
                className="bg-[#B85C38] text-[#F7F5F0] px-5 py-2.5 rounded-full font-mono-a text-[10px] uppercase tracking-[0.15em] hover:bg-[#E7C6A5] hover:text-[#2C2A28] transition-colors disabled:opacity-50 shrink-0">
                {subLoading ? "…" : "Subscribe"}
              </button>
            </form>
          )}
          <a href="https://instagram.com/agelesswomanretreats" target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link"
            className="mt-4 inline-block font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#F7F5F0]/60 hover:text-[#E7C6A5] transition-colors">
            Follow &amp; be part of the journey → @agelesswomanretreats
          </a>
        </div>
        <div className="flex flex-wrap content-start gap-x-8 gap-y-4 font-mono-a text-[11px] uppercase tracking-[0.18em] text-[#F7F5F0]/60 md:max-w-[220px]">
          <button onClick={() => scrollTo("intention")} className="hover:text-[#E7C6A5]">Intention</button>
          <button onClick={() => scrollTo("reserve")} className="hover:text-[#E7C6A5]">Priority List</button>
          <a href="/priority" data-testid="footer-priority-link" className="hover:text-[#E7C6A5]">Priority List</a>
          <a href="/workshop" data-testid="footer-workshop-link" className="hover:text-[#E7C6A5]">Free Workshop</a>
          <a href="/dolce-vita-audit" data-testid="footer-audit-link" className="hover:text-[#E7C6A5]">Dolce Vita Audit</a>
          <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="footer-terms-link" className="hover:text-[#E7C6A5]">Terms</a>
          <a href="mailto:hello@agelesswomanretreat.com" className="hover:text-[#E7C6A5]">Contact</a>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-12 pt-8 border-t border-[#F7F5F0]/10 font-mono-a text-[10px] uppercase tracking-[0.15em] text-[#F7F5F0]/40">
        © {new Date().getFullYear()} The Ageless Woman · Florence & Tuscany. All rights reserved.
      </div>
    </footer>
    </>
  );
}

export default function Home() {
  useLenis();
  return (
    <main>
      <Nav />
      <Hero />
      <TextMarquee />
      <Intention />
      <Transformation />
      <WhatsCosting />
      <ApplyCta bg="#2C2A28" dark />
      <Team />
      <WhatMakesDifferent />
      <QuoteBand
        quote="Pleasure is essential and necessary as a right to enjoy life in every moment. It's not to be earned, is not a reward,"
        highlight="is not a trophy."
        source="Monica Pandele · The Joyful Rebellion"
      />
      <Location />
      <SensoryMarquee />
      <Attractions />
      <QuoteBand
        quote="You cannot create from emptiness. You can perform from depletion — we are all experts at it —"
        highlight="but it is never sustainable."
        source="Monica · The Joyful Rebellion"
      />
      <Journey />
      <LivedNotListed />
      <QuoteBand
        quote="Let joy be the"
        highlight="fuel, and play be the way."
        source="Monica · The Joyful Rebellion"
      />
      <Includes />
      <ApplyCta bg="#EAE5D9" />
      <OurWhy />
      <QuoteBand
        quote="Life loves the"
        highlight="liver of it!"
        source="Natalie · The Ageless Woman"
      />
      <Testimonials />
      <Faq />
      <Connect />
      <IncludedChecklist />
      <Reserve />
      <Footer />
    </main>
  );
}
